// Plan 08-06 — Friend-invite suggestion modal.
//
// Per DEC-027 step 5 + UI-SPEC §"Friend invite (QR + code)":
//   Fires AFTER match 3 (real, post-tutorial) if $onboarding.friendInviteShown
//   is false. One-shot — tapping Later marks friendInviteShown=true and the
//   modal never auto-fires again.
//
// Anatomy: standard modal + header "Want to play with a friend?" (Manrope) +
// body "Share your code. Same court, two minds." (key home.friends.empty.body)
// + Primary CTA "Share my code" default-blue btn-md → routes to /profile?invite=true
// (placeholder until profile screen lands) + "Later" CTA dismisses.

import type { JSX } from 'react'
import { FormattedMessage } from 'react-intl'

import { useRouter } from '../../router/router.js'
import { markFriendInviteShown } from '../../stores/onboarding.js'
import { Dialog } from '../components/Dialog'

interface FriendInviteSuggestionProps {
  open: boolean
  onClose: () => void
}

export function FriendInviteSuggestion({
  open,
  onClose,
}: FriendInviteSuggestionProps): JSX.Element | null {
  const router = useRouter()

  function handleLater(): void {
    markFriendInviteShown()
    onClose()
  }

  if (!open) return null

  function handleShare(): void {
    markFriendInviteShown()
    router.navigate('/profile?invite=true')
    onClose()
  }

  return (
    // Center-dialog flavor (UI-SPEC §5): scrim + popIn + focus trap + dismissal
    // live in <Dialog>/ModalScrim. Escape maps to handleLater so
    // friendInviteShown is marked before dismiss.
    <Dialog
      onClose={handleLater}
      testId="friend-invite-suggestion"
      labelledBy="friend-invite-title"
      panelStyle={{ overflow: 'hidden' }}
    >
      <div>
        <div
          style={{
            height: '48px',
            backgroundColor: 'var(--color-paper-2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <h2
            id="friend-invite-title"
            style={{
              margin: 0,
              fontFamily: 'var(--font-family-display)',
              fontWeight: 700,
              fontSize: 'var(--font-size-18)',
              color: 'var(--color-text-primary)',
            }}
          >
            <FormattedMessage
              id="friend.invite.suggestion.title"
              defaultMessage="Want to play with a friend?"
            />
          </h2>
        </div>
        <div style={{ padding: '20px' }}>
          <p
            style={{
              margin: 0,
              fontFamily: 'var(--font-family-body)',
              fontWeight: 500,
              fontSize: 'var(--font-size-14)',
              lineHeight: 1.5,
              color: 'var(--color-text-primary)',
            }}
          >
            <FormattedMessage
              id="home.friends.empty.body"
              defaultMessage="Share your code. Same court, two minds."
            />
          </p>
          <div
            style={{
              marginTop: '20px',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
            }}
          >
            <button
              type="button"
              onClick={handleLater}
              data-cta="friend-invite-later"
              style={{
                height: '48px',
                padding: '0 20px',
                backgroundColor: 'var(--color-card)',
                border: 'none',
                boxShadow:
                  'inset 0 0 0 2px color-mix(in srgb, var(--color-surface-stroke) 40%, transparent), var(--shadow-card)',
                borderRadius: '12px',
                fontFamily: 'var(--font-family-display)',
                fontWeight: 700,
                fontSize: 'var(--font-size-16)',
                color: 'var(--color-text-primary)',
                cursor: 'pointer',
              }}
            >
              <FormattedMessage
                id="onboarding.cta.signup_later"
                defaultMessage="Later"
              />
            </button>
            <button
              type="button"
              onClick={handleShare}
              data-cta="friend-invite-share"
              style={{
                height: '48px',
                padding: '0 24px',
                backgroundColor: 'var(--color-card)',
                border: 'none',
                borderRadius: '12px',
                fontFamily: 'var(--font-family-display)',
                fontWeight: 700,
                fontSize: 'var(--font-size-16)',
                color: 'var(--color-text-primary)',
                cursor: 'pointer',
                boxShadow:
                  '0 6px 0 var(--color-surface-deep), var(--shadow-card)',
              }}
            >
              <FormattedMessage
                id="friend.invite.cta"
                defaultMessage="Share my code"
              />
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  )
}
